import React from 'react'
import { useContext } from 'react'

type CatchErrorContext = { setError: (error: Error) => void }

const context = React.createContext<CatchErrorContext>(
    {
        // Initialize as null so that we can warn the developer if they
        // forget the provider.
        setError: null
    } as any
)

/**
 * This should wrap the hierarchy you want to be able to capture
 * errors in.
 */
export const CatchErrorProvider = context.Provider

/**
 * Helper hook to propagate errors throw by function up to the global
 * error error context.
 *
 * @example
 * const catchError = useCatchError()
 * <Button onClick={catchError(() => doClick())} />
 */
export function useCatchError() {

    const catchErrorContext = useContext(context)

    if (catchErrorContext.setError == null) {
        // We depend on an outer context to catch our error,
        // if it doesn't exist the developer needs to be
        // notified.
        console.error(`You need to provide an error context to use this hook`)
    }

    // This returns the hook, innerFn is what we'll be passing to this
    // hook when we use it.
    return (innerFn: any) => {

        // This is the wrapper around the innerFn we're trying to
        // call. It will call innerFn for us, but with a try-catch
        // block that will handle any errors for us automatically.
        //
        // If no errors are encountered, we just proceed normally.
        return (...args: any[]) => {

            try {

                // Try calling the function normally
                return innerFn(...args)

            } catch (error) {
                if (error instanceof Error) {
                    catchErrorContext.setError(error)
                }
            }
        }

    }
}
